// Mirrors Ai1Assistant.App.Domain.Models.PaaExtraction / PaaRow.
import type { PropertyHeader } from "@/features/upload/property/types";

export interface PaaRow {
  id: string;
  /** 1-based PDF page this row came from; null for manually-added rows. */
  sourcePage?: number | null;
  sheetId: string;
  revision: string; // "0.00" | "0.01" | ...
  action: PaaAction;
  /** True once the user picks the action by hand, so editing the revision
   *  won't silently re-derive (and overwrite) it. */
  actionLocked?: boolean;
  originalNewOmitPageId: string;
  supersedingPageId: string;
  /** Brief "what changed" note, set when an OLD (approved) set is also uploaded. */
  changeNote?: string | null;
  /** The note exactly as the comparison engine wrote it, never modified by an
   *  edit. At generate time the server records (aiChangeNote, changeNote) as a
   *  correction whenever they differ — that pair is how we learn how these
   *  notes should actually read. Set by the server only; never printed. */
  aiChangeNote?: string | null;
  /** True when an approved set was uploaded but no matching sheet was found in
   *  it to compare against — the note stays empty and the UI shows a hint. */
  oldSheetMissing?: boolean;
  /** Baseline override: which APPROVED-set sheet (base id) to compare against.
   *  Null/undefined = auto (same base as sheetId). When set, even "N" rows get
   *  a real diff (the broken-out / renumbered sheet case). */
  compareWith?: string | null;
  /** Optional context for the AI narrator on this row's comparison. */
  compareHint?: string | null;
  /** The approved-set sheet actually used by the LAST comparison (display). */
  comparedTo?: string | null;

  /** The operator has looked at this sheet and is satisfied with it.
   *
   *  CLIENT ONLY, and deliberately so. It never reaches the server, is never
   *  stored and is never printed — it exists to answer "where was I", which is
   *  the thing she has no way to know today. It survives a `row` event because
   *  applyRowNotePatch merges field by field rather than replacing the row.
   *
   *  Cleared automatically when the note underneath it changes: what she agreed
   *  to is no longer what the row says. Same rule as a changed file losing its
   *  "viewed" tick on a pull request, and for the same reason — a confirmation
   *  that outlives the thing it confirmed is worse than none. */
  reviewed?: boolean;

  /** She has confirmed what this page supersedes — the FIRST decision in a
   *  filing, and the one everything downstream is built on.
   *
   *  Client only, like `reviewed`. The decision itself is not: it is written to
   *  `compareWith`, which the comparison diffs against and which
   *  PaaCombinePlanner treats as its highest-priority placement signal
   *  (OperatorAnchor). This flag only records that a human has looked. */
  pairingConfirmed?: boolean;

  /**
   * How she settled a same-number collision: this page is marked new, and a
   * sheet of the same number is already in the approved set.
   *
   * There is deliberately NO default. Left alone the combined set would carry
   * BOTH versions of the drawing — which is what the app used to do silently,
   * with a warning on a row buried at the end of a ninety-page list, after the
   * description pass had already been paid for. Filing two versions of one
   * sheet is the worst outcome this review exists to prevent, so it is only ever
   * reached by someone choosing it on purpose.
   *
   *   "updated" — the updated page supersedes the approved one (action becomes
   *               S). The ordinary answer.
   *   "both"    — both are filed. Rare and legitimate (an architect can reuse a
   *               number for a different drawing), never assumed.
   *
   * Keeping the approved sheet instead needs no value here: that page simply is
   * not part of this filing, so the row leaves the AI1.
   *
   * Client only. The OUTCOME lives in `action`, which the planner already
   * understands; this records that a human decided, so the prompt stops asking.
   */
  sameNumberChoice?: "updated" | "both";

  /** What the deterministic router concluded, and its one-line reasoning. Null
   *  until this row has been compared. Shown so the operator can question a
   *  verdict — one nobody can question is one everybody rubber-stamps. */
  verdict?: PaaChangeVerdict | null;
  verdictExplanation?: string | null;
  /** The content says this sheet changed but its printed revision never moved:
   *  the architect forgot to bump it. Left alone the AI1 goes out with the wrong
   *  action and revision for the sheet. */
  revisionLooksStale?: boolean;
  /** This row cannot be finished without a human. The case that matters: the
   *  sheet changed and nothing readable differed — a graphical-only revision.
   *  The note must not go out blank OR claiming no change. */
  needsReview?: boolean;
  reviewReason?: string | null;

  /** What the comparison saw on this sheet, in plain English, written by the same
   *  call that wrote the note.
   *
   *  Why this is here: the operator signs a legal document having seen one line
   *  of prose, and to check it they otherwise have to open the drawings and hunt.
   *  Worse, they can't tell "we understood this sheet and phrased it tersely"
   *  from "we missed half of it". On 1056 A-100 we knew the exercise room went
   *  1,170 SF to 676 SF and none of it reached the screen — while the note the
   *  office filed for that sheet said "enlarged".
   *
   *  This started life as a LIST of findings and had to be thrown away: the
   *  record diff is 40-75 entries on a real sheet, and rendering it as rows put
   *  an unreadable wall under every note. Prose leads with the values that moved
   *  and disposes of the other forty in a clause. Never printed on the form,
   *  which is what lets it quote values the note may not. */
  readSummary?: string | null;
  /** Areas of the drawing that differ with nothing readable to explain them.
   *  The number that would have caught the bike room relocating on A-100. */
  unexplainedAreas?: number;
  /** Entries one reading produced and the other didn't, with no value to compare.
   *  Measured by us rather than judged by the model, and shown for the same
   *  reason: a big number here means this sheet's readings barely agreed, so the
   *  summary above is weak evidence and the drawings are the answer. */
  readOnOneSide?: number;
  /** What we could not see on this sheet, stated only where it applies. */
  blindSpot?: string | null;
}

/** Mirrors Ai1Assistant.App.Domain.Models.PaaChangeVerdict. */
export type PaaChangeVerdict =
  /** Nothing says this sheet changed — reissued with the set, not revised. */
  | "Unchanged"
  /** Changed, and the geometry pass could say where. */
  | "ChangedLocalized"
  /** Changed, but redrawn or re-plotted, so areas cannot be isolated. */
  | "ChangedWholeSheet"
  /** Could not be compared at all (typically a scanned approved set). NOT a
   *  verdict of "unchanged" — the tool could not look. */
  | "Undetermined";

export function isChangedVerdict(v: PaaChangeVerdict | null | undefined): boolean {
  return v === "ChangedLocalized" || v === "ChangedWholeSheet";
}

export type PaaAction = "N" | "S" | "O";

/** One root design decision of the amendment (e.g. "new base height").
 *
 *  These are for the REVIEW PAGE: four reasons are checkable where forty notes
 *  get rubber-stamped. Nothing printed depends on them — the model writes a cause
 *  into a note in its own words, or leaves it out. The list round-trips on every
 *  annotate call so a per-row re-compare reuses the same ids. */
export interface PaaDriver {
  id: number;
  phrase: string;
  /** Primary (T/Z) sheet the decision was read from — the receipt that makes a
   *  driver reviewable by someone who didn't draw the plans. Blank when the
   *  derivation couldn't point at one; never guessed. */
  sourceSheet?: string | null;
  /** The change on that sheet that shows it, copied from the diff. */
  evidence?: string | null;
  /** Sheet ids whose notes actually cite it — the blast radius, computed from
   *  what was substituted, not from what the model claimed. */
  citedOn?: string[] | null;
  /** True when a human wrote or edited this phrase; it is then never re-worded
   *  by a later derivation. */
  userDefined?: boolean;
}

/** Next free driver id — ids are the key notes cite, so a new one must never
 *  collide with an existing (or previously removed) driver in this run. */
export function nextDriverId(drivers: PaaDriver[] | null | undefined): number {
  return (drivers ?? []).reduce((max, d) => Math.max(max, d.id), 0) + 1;
}

export interface PaaExtraction extends PropertyHeader {
  rows: PaaRow[];
  /** Optional operator-stated design intent, seeding the "what changed"
   *  driver derivation as the authoritative story. Never printed. */
  designIntent?: string | null;
  /** Amendment drivers derived by the last comparison run. Never printed, and
   *  no longer shown: the panel that displayed them was replaced by `gist`.
   *  Still round-tripped so a rewrite or per-row re-compare reuses the same ids
   *  and phrasing rather than renumbering the story. */
  drivers?: PaaDriver[] | null;
  /** The amendment in a short paragraph, written by the notes call and editable
   *  above the sheet table. NEVER printed.
   *
   *  This replaced the reasons list. That panel existed to check a claim the
   *  FORM was about to make — "revised zoning analysis per revised building
   *  bulk" — because a cause is the one thing on an AI1 nobody can verify
   *  against a drawing. Once the office can switch that clause off
   *  (AiParsing:WhatChangedCauseAttribution), it was reviewing an internal
   *  intermediate. What a person still needs before scanning thirty rows is the
   *  gist, and one paragraph gives it where five phrases with receipts did not.
   *
   *  Editing it feeds the next composition, so correcting the story once is
   *  still how you fix notes without editing them one at a time. */
  gist?: string | null;
  /** The operator rewrote the gist. A rewrite then leaves their words alone —
   *  it still reads them, but never replaces them with its own summary. */
  gistUserDefined?: boolean;
  /** The DOB filing type for the AI1's download filename — "PL", "FO", "GC"
   *  (SPE-15). Seeded from the filing's own sheet numbers, and blank whenever
   *  those cannot say: most often an architectural set, where the prefixes
   *  cannot separate general construction from energy. Blank is normal and
   *  frequent, and produces exactly the filename this app produced before. */
  filingType?: string | null;
  /** The operator typed it themselves, so it wins over anything we read and a
   *  re-reading must not overwrite it. Same contract as `gistUserDefined`.
   *
   *  It also marks the value as EVIDENCE: nobody has confirmed the sheet-prefix
   *  mapping, so an override is recorded at generate
   *  (CorrectionType.PaaFilingType) and those rows are how the open questions
   *  in docs/SHEET_PREFIX_MAPPING.md get answered by use. */
  filingTypeUserDefined?: boolean;
}

export function makeEmptyRow(): PaaRow {
  return {
    id: cryptoRandomId(),
    sourcePage: null,
    sheetId: "",
    revision: "0.00",
    action: "N",
    actionLocked: false,
    originalNewOmitPageId: "",
    supersedingPageId: "",
  };
}

// Parses "0.NN" -> NN as integer minor. Returns 0 on garbage input rather
// than NaN so downstream arithmetic stays sane during user edits. The pattern
// already covers "0.NN", ".NN" and bare "NN".
export function parseRevisionMinor(revision: string): number {
  const match = /^0?\.?(\d{1,2})$/.exec(revision.trim());
  return match ? parseInt(match[1], 10) : 0;
}

export function formatRevision(minor: number): string {
  const safe = Math.max(0, Math.min(99, Math.trunc(minor)));
  return `0.${safe.toString().padStart(2, "0")}`;
}

// Page IDs always carry the 2-digit revision suffix (".00", ".01", …):
// a new sheet's page id is "A-104.00", a first revision is "A-101.01".
export function pageIdFor(sheetId: string, minor: number): string {
  const base = sheetId.trim();
  if (!base) return "";
  if (minor < 0) return base;
  return `${base}.${minor.toString().padStart(2, "0")}`;
}

// Deterministic page IDs from sheet + revision + action (no manual override —
// the review table doesn't expose these columns anymore).
export function pageIdsFor(
  sheetId: string,
  revision: string,
  action: PaaAction,
): { originalNewOmitPageId: string; supersedingPageId: string } {
  const minor = parseRevisionMinor(revision);
  if (action === "S") {
    return {
      originalNewOmitPageId: pageIdFor(sheetId, Math.max(0, minor - 1)),
      supersedingPageId: pageIdFor(sheetId, minor),
    };
  }
  // N (new) or O (omit): single page id, no superseding.
  return {
    originalNewOmitPageId: pageIdFor(sheetId, minor),
    supersedingPageId: "",
  };
}

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `r-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}
